import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, FlexContainer, Spinner } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { FilingComponentsProps } from 'Containers/Filing/types';
import {
  useGetInfoFilingUser,
  usePatchFilingUser,
  usePostSubmitFiling,
} from 'Containers/Filing/hooks';
import { FRONT_PLANS } from 'Constants/plans';
import { ResultTypes } from 'Types/common';
import FilingSubmit from '../FilingSubmit';
import { PayLaterInfo } from './PayLaterInfo';
import * as Styled from './styles';
import InstallmentsList from './InstallmentsList';

const ReviewForm = ({ onChangeStep }: FilingComponentsProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const [installments, setInstallments] = useState<number>(1);
  const { taxReturnType, taxReturnResult, plan, isInstantRefund } = useSelector(routeStateSelector);
  const { mutate, isLoading } = usePatchFilingUser();
  const data = useGetInfoFilingUser();
  const { mutate: submitDeclaration } = usePostSubmitFiling(onChangeStep);
  const handleOnClick = (): void => {
    if (data?.installmentsList?.length > 0) {
      const dataInfo = { installments, CLABE: data?.CLABE };
      mutate(dataInfo, {
        onSuccess: () => {
          submitDeclaration({});
        },
      });
    } else {
      submitDeclaration({});
    }
  };
  if (isLoading) {
    return (
      <FlexContainer justify="center" align="center">
        <Spinner size="M" justify="center" align="center" />
      </FlexContainer>
    );
  }

  return (
    <FlexContainer direction="column" gap="dimension_16">
      <Text type="headline" size="XS_L_M" color="dark" align="center">
        {t(`ReviewForm.header.${taxReturnType}`, { context: isInstantRefund && 'instantRefund' })}
      </Text>
      <FlexContainer direction="column" justify="center" gap="dimension_16">
        <Text size="S_S_S" color="dark" type="paragraphs" align="center">
          {t('ReviewForm.reviewInfo', { context: isInstantRefund && 'instantRefund' })}
        </Text>
        {plan && plan?.name === FRONT_PLANS.FLEX ? (
          <PayLaterInfo result={taxReturnResult} />
        ) : (
          <FlexContainer direction="column" gap="dimension_16">
            <Text type="paragraphs" size="M_L_S" color="dark">
              {t(`ReviewForm.${taxReturnType}.devolution`)}
            </Text>
            <Styled.InfoContent isDisabled size="S" hasArrow={false}>
              <Text type="paragraphs" size="M_L_S" color="dark">
                {YayoyakiFormatter.formatAmountMxn(Math.abs(taxReturnResult))}
              </Text>
            </Styled.InfoContent>
          </FlexContainer>
        )}
        {taxReturnType === ResultTypes.toReturn && data.CLABE && (
          <FlexContainer direction="column" gap="dimension_16">
            <Text type="paragraphs" size="M_L_S" color="dark">
              {t('ReviewForm.toReturn.clabe')}
            </Text>
            <Styled.InfoContent isDisabled size="S" hasArrow={false}>
              <Text type="paragraphs" size="M_L_S" color="dark">
                {data.CLABE}
              </Text>
            </Styled.InfoContent>
          </FlexContainer>
        )}
        {taxReturnType === ResultTypes.toPay && data.installmentsList && (
          <InstallmentsList data={data.installmentsList} setInstallments={setInstallments} />
        )}
      </FlexContainer>
      <FilingSubmit handleSubmit={handleOnClick} buttonLabel="ReviewForm.CTA" />
    </FlexContainer>
  );
};

export default ReviewForm;
